import React, { useEffect } from 'react';
import { ModalMutateFeedbackSubjectProps, MutateFormFields } from './types';
import { Button } from '@vtbhome/juicyfront';
import { Body, Footer, InputGroup } from './modal-mutate-feedback-subject.styles';
import { Field, Form } from 'react-final-form';
import { validate } from '../../lib/form-validate';
import { Typography } from '@shared/ui/typography/typography';
import { InputText } from '@shared/ui/input-text/input-text';
import { getQueryError } from '@shared/lib/utils/get-query-error/get-query-error';
import { CACHE_KEY_PRODUCT_CREATE, CACHE_KEY_PRODUCT_UPDATE } from '@entities/product/config/api-constants';
import { InputNumber } from '@shared/ui/input-number/input-number';
import { RadioGroup } from '@shared/ui/radio-group/radio-group';
import { normalizeInitialValues } from '../../lib/normalize-initial-values';
import { normalizeSubmitValues } from '../../lib/normalize-submit-values';
import { EditFormKeys } from '../../config/form-constants';
import { NetworkStateNew } from '@shared/ui/network-state-new/network-state-new';
import { useFeedbackSubjectDetailSharedQuery } from '@entities/feedback-subject/libs/hooks/use-feedback-subject-detail-shared-query/use-feedback-subject-detail-shared-query';
import {
    useCreateFeedbackSubjectMutation,
    useUpdateFeedbackSubjectMutation,
} from '@entities/feedback-subject/api/feedback-subject-api';
import { LinkAdministrators } from '../link-administrators/link-administrators';
import { Modal } from '@shared/ui/modal/modal';
import { useResetModalForm } from '@shared/lib/hooks/use-reset-modal-form/use-reset-modal-form';

export const ModalMutateFeedbackSubject: React.FC<ModalMutateFeedbackSubjectProps> = ({
    feedbackSubjectId,
    action = 'create',
    isShowModal,
    onClose,
    onAccept,
}) => {
    const [getFeedbackSubjectDetail, getFeedbackSubjectDetailResult] =
        useFeedbackSubjectDetailSharedQuery(feedbackSubjectId);
    const [createFeedbackSubject, createFeedbackSubjectResult] = useCreateFeedbackSubjectMutation({
        fixedCacheKey: CACHE_KEY_PRODUCT_CREATE,
    });
    const [editFeedbackSubject, editFeedbackSubjectResult] = useUpdateFeedbackSubjectMutation({
        fixedCacheKey: CACHE_KEY_PRODUCT_UPDATE,
    });
    const feedbackSubjectDetailInfo = getFeedbackSubjectDetailResult.data;
    const error = getQueryError(getFeedbackSubjectDetailResult);
    const initialValues = normalizeInitialValues(feedbackSubjectDetailInfo);
    const formRef = useResetModalForm<MutateFormFields, typeof initialValues>(isShowModal);

    const isCreateAction = action === 'create';
    const isLoadingInfo =
        getFeedbackSubjectDetailResult.isFetching ||
        getFeedbackSubjectDetailResult.isLoading ||
        (getFeedbackSubjectDetailResult.isUninitialized && !isCreateAction);
    const isLoadingMutate = editFeedbackSubjectResult.isLoading || createFeedbackSubjectResult.isLoading;

    const handleSubmit = async (fields: MutateFormFields) => {
        let error = false;
        if (feedbackSubjectId && action === 'edit') {
            const result = await editFeedbackSubject({
                id: feedbackSubjectId,
                ...normalizeSubmitValues(fields),
            });
            getFeedbackSubjectDetail(feedbackSubjectId, false);
            error = Boolean(result.error);
        } else if (isCreateAction) {
            const result = await createFeedbackSubject(normalizeSubmitValues(fields));
            error = Boolean(result.error);
        }
        if (!error) {
            onAccept();
        }
    };

    useEffect(() => {
        if (isShowModal && feedbackSubjectId) {
            getFeedbackSubjectDetail(feedbackSubjectId);
        }
    }, [isShowModal, feedbackSubjectId]);

    return (
        <Form
            onSubmit={handleSubmit}
            validate={validate}
            initialValues={initialValues}
            render={(form) => {
                formRef.current = form;
                return (
                    <Modal
                        isShow={isShowModal}
                        variant='drawer'
                        size='l'
                        header={
                            <Typography size='h6'>{`${isCreateAction ? 'Создание' : 'Редактирование'} темы`}</Typography>
                        }
                        footer={
                            <Footer>
                                <Button disabled={isLoadingMutate} buttonType='light' onClick={onClose}>
                                    Отменить
                                </Button>
                                <Button
                                    preloader={isLoadingMutate}
                                    disabled={isLoadingMutate}
                                    buttonType='primary'
                                    onClick={form.handleSubmit}>
                                    Подтвердить
                                </Button>
                            </Footer>
                        }
                        onClose={onClose}>
                        <Body>
                            <NetworkStateNew
                                size='s'
                                isLoading={isLoadingInfo}
                                isError={Boolean(error)}
                                isInitialized={!isLoadingInfo}
                                isFiltered={false}
                                hasData={isCreateAction || Boolean(feedbackSubjectDetailInfo)}
                                errorProps={{
                                    title: 'Ошибка загрузки тем обращений',
                                    description: error?.message,
                                }}>
                                <InputGroup>
                                    <Field
                                        name={EditFormKeys.Title}
                                        render={({ input, meta }) => (
                                            <InputText
                                                {...input}
                                                label='Название темы'
                                                placeholder='Введите название темы'
                                                required
                                                error={meta.touched && meta.error}
                                            />
                                        )}
                                    />
                                    <Field
                                        name={EditFormKeys.Description}
                                        render={({ input, meta }) => (
                                            <InputText
                                                {...input}
                                                label='Описание'
                                                placeholder='Введите описание'
                                                error={meta.touched && meta.error}
                                            />
                                        )}
                                    />
                                    <Field
                                        name={EditFormKeys.Code}
                                        render={({ input, meta }) => (
                                            <InputText
                                                {...input}
                                                label='Системное название (код)'
                                                placeholder='Введите системное название'
                                                required
                                                error={meta.touched && meta.error}
                                            />
                                        )}
                                    />
                                    <Field
                                        name={EditFormKeys.Sort}
                                        render={({ input, meta }) => (
                                            <InputNumber
                                                {...input}
                                                label='Сортировка'
                                                placeholder='Введите число'
                                                required
                                                error={meta.touched && meta.error}
                                            />
                                        )}
                                    />
                                    <RadioGroup
                                        name={EditFormKeys.Active}
                                        label='Состояние темы'
                                        fields={[
                                            {
                                                label: 'Активный',
                                                value: 'true',
                                            },
                                            {
                                                label: 'Скрытый',
                                                value: 'false',
                                            },
                                        ]}
                                    />
                                    <Field
                                        name={EditFormKeys.Users}
                                        render={({ input }) => <LinkAdministrators {...input} />}
                                    />
                                </InputGroup>
                            </NetworkStateNew>
                        </Body>
                    </Modal>
                );
            }}
        />
    );
};
